/* ============
 * Edit Page
 * ============
 *
 * This is the page for editing a product group.
 */
import { mapGetters } from 'vuex';
import productPackagingService from './../../../../services/productPackaging';
import packagingMaterialService from '../../../../services/packagingMaterial';
import packagingTypeService from '../../../../services/packagingType';
import productPackagingTransformer from './../../../../transformers/productPackaging';
import materialService from './../../../../services/material';
import Forms from './../../../../utils/forms/forms';
import store from './../../../../store';
import exciseGroupService from './../../../../services/exciseGroup';
import depositSystemService from './../../../../services/depositSystem';
import productDepositService from './../../../../services/productDeposit';

export default {
  data() {
    return {
      productsCount: 0,
      form: new Forms({
        name: {
          value: '',
          type: 'text',
        },
        volume: {
          value: '',
          type: 'text',
        },
        qtyInCrate: {
          value: '',
          type: 'number',
        },
        packagingTypeId: {
          value: '',
          type: 'select',
        },
        packagingMaterialId: {
          value: '',
          type: 'select',
        },
        materialId: {
          value: '',
          type: 'select',
        },
        crateMaterialId: {
          value: '',
          type: 'select',
        },
      }),

      inheritFieldsform: new Forms({
        excisePackagingGroup: {
          value: '',
          type: 'select',
        },
        exciseBasicGroup: {
          value: '',
          type: 'select',
        },
        depositSystemId: {
          value: '',
          type: 'select',
        },
        productDepositId: {
          value: '',
          type: 'select',
        },
        overrideProducts: {
          value: false,
          type: 'checkbox',
        },
      }),
      loading: false,
      initLoad: false,
    };
  },

  mounted() {
    Promise.all([
      exciseGroupService.basic(),
      exciseGroupService.packaging(),
      depositSystemService.all(),
      productDepositService.all(),
      productPackagingService.all(),
      packagingMaterialService.all(),
      packagingTypeService.all(),
      exciseGroupService.all(),
      materialService.all(),
    ]).then(() => {
      this.loading = false;
      if (!this.hasProduction) {
        this.form.hideField('materialId');
        this.form.hideField('crateMaterialId');
      }
      this.inheritFieldsform.setOptions('depositSystemId', this.depositSystems.all.map(depositSystem => ({ id: depositSystem.id, name: depositSystem.name })));
      this.inheritFieldsform.setOptions('productDepositId', this.productDeposits.all.map(deposit => ({ id: deposit.id, name: deposit.name })));
      this.show();
    });
  },

  computed: {
    ...mapGetters({
      productPackagings: 'allProductPackagings',
      packagingMaterials: 'allPackagingMaterials',
      packagingTypes: 'allPackagingTypes',
      materials: 'allMaterials',
      account: 'account',
      exciseBasicGroups: 'allExciseBasicGroups',
      excisePackagingGroups: 'allExcisePackagingGroups',
      depositSystems: 'allDepositSystems',
      productDeposits: 'allProductDeposits',
      // duties: 'allExciseDuties',
    }),
    hasProduction() {
      if (this.account.modules.length > 0) {
        return this.account.modules.find(module => module.name === 'Production');
      }
      if (this.account.allowProducerBatches) return true;
      return false;
    },
  },

  watch: {
    packagingMaterials() {
      this.form.setOptions('packagingMaterialId', this.packagingMaterials.all);
    },
    packagingTypes() {
      this.form.setOptions('packagingTypeId', this.packagingTypes.all);
    },
    materials() {
      this.form.setOptions('materialId', this.materials.filter(m => m.materialType === 'Packaging').map(item => ({ id: item.id, name: item.name })));
      this.form.setOptions('crateMaterialId', this.materials.filter(m => m.materialType === 'Crates and boxes').map(item => ({ id: item.id, name: item.name })));
    },

    'form.packagingMaterialId': function (value) {
      this.assignExcise(value);
    },

    'inheritFieldsform.depositSystemId': function (value) {
      if (value != '') {
        this.inheritFieldsform.showField('productDepositId');
      } else {
        this.inheritFieldsform.hideField('productDepositId');
      }
    },
  },

  methods: {

    assignExcise(value) {
      const result = this.packagingMaterials.all.filter(item => item.id === parseInt(value));
      if (result.length != 0) {
        if (!this.initLoad) {
          this.inheritFieldsform.setFieldValue('exciseBasicGroup', '');
          this.inheritFieldsform.setFieldValue('excisePackagingGroup', '');
        } else {
          this.initLoad = false;
        }
        let basic = this.exciseBasicGroups.filter(item => item.codeId === result[0].baseExciseCodeId);
        let packaging = this.excisePackagingGroups.filter(item => item.codeId === result[0].environmentExciseCodeId);
        basic = basic.map(c => ({ id: c.id, name: `${c.description}` }));
        packaging = packaging.map(c => ({ id: c.id, name: `${c.description}` }));
        basic.unshift({ id: '', name: '' });
        packaging.unshift({ id: '', name: '' });
        this.inheritFieldsform.setOptions('exciseBasicGroup', basic);
        this.inheritFieldsform.setOptions('excisePackagingGroup', packaging);
      }
    },

    /**
     * Method to update the product group.
     */
    update() {
      if (this.inheritFieldsform.overrideProducts) {
        this.$confirm(
          this.$t('form.productPackagings.overrideProductsConfirm.description'),
          this.$t('form.productPackagings.overrideProductsConfirm.title'), {
            confirmButtonText: 'I Accept',
            cancelButtonText: 'Cancel',
            type: 'danger',
          }).then(() => {
            this.updatePackaging();
          }).catch(() => {});
      } else {
        this.updatePackaging();
      }
    },

    updatePackaging() {
      this.form.loading = true;
      const data = { ...this.form.data(), ...this.inheritFieldsform.data() };
      const productPackaging = productPackagingTransformer.send(data);
      productPackagingService.update(store.state.route.params.id, productPackaging)
        .then(() => {
          this.form.loading = false;
          this.$message({
            message: this.$t('static.productPackagings.edit.success'),
            type: 'success',
          });
          this.$router.push({
            name: 'productPackagings.index',
          });
        }).catch((errors) => {
          this.form.loading = false;
          this.form.recordErrors(errors);
        });
    },

    /**
     * Fetches the product group object for editing.
     */
    show() {
      this.loading = true;
      productPackagingService.show(store.state.route.params.id).then((productPackaging) => {
        this.initLoad = true;
        this.form.assignData(productPackaging);
        this.inheritFieldsform.assignData(productPackaging);
        this.productsCount = productPackaging.productsCount;
        this.loading = false;
      });
    },

    destroy() {
      productPackagingService.destroy(store.state.route.params.id)
      .then(() => {
        this.$message({
          message: this.$t('static.productPackagings.edit.delete'),
          type: 'success',
        });
        this.$router.push({
          name: 'productPackagings.index',
        });
      });
    },
  },

  components: {
    VForm: require('./../../../../components/form/form.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/productPackagings/edit/edit.js